import type { Board } from './lib';
import type { Lang } from './i18n';

export type Example = { board: Board; blurb: string; sample: number[] };

export const EXAMPLES: Record<Lang, Example[]> = {
  cs: [
    {
      blurb: 'Pro všechny, kdo sedí ve volební komisi. Odškrtávej, co se v místnosti fakt stane.',
      sample: [1, 6, 9, 15],
      board: {
        title: 'Volební bingo',
        size: 4,
        free: false,
        shuffle: false,
        cells: [
          'eDoklady nefungují',
          'Volič se hlásí adresou místo jména',
          'Volič komentuje politickou situaci',
          'Volič se snaží do urny hodit hlas mimo obálku',
          'Focení se u volební urny',
          'Manželé se spolu snaží jít za plentu',
          'Volič dostane od komise lístky a jde si je nastudovat mimo volební místnost',
          '„Kolik vám přišlo lidí?“',
          'Volební komise neumí abecedu',
          '„Koho mám volit?“',
          'Dítě hází obálku do urny',
          'Volič se snaží prokázat řidičákem',
          'Volič jde za volební plentu s dítětem',
          '„Mám tu obálku zalepit?“',
          'Neplatná občanka / pas',
          'Někdo něco rozlije',
        ],
      },
    },
    {
      blurb: 'Na online poradu, která mohla být e-mail. Každý dostane jinak zamíchanou kartu.',
      sample: [0, 7, 12, 18],
      board: {
        title: 'Porada bingo',
        size: 5,
        free: true,
        shuffle: true,
        cells: [
          '„Slyšíte mě?“',
          '„Máš vypnutý mikrofon“',
          '„Pošlu to mailem“',
          '„Vrátíme se k tomu“',
          '„Vidíte moji obrazovku?“',
          '„Kdo to má na starosti?“',
          '„Musím běžet, mám další call“',
          'Synergie',
          'Někdo u kamery jí',
          'Pes v pozadí',
          'Zamrzlé video',
          '„Dáme si na to separátní call“',
          'Ozvěna',
          '„To je dobrá otázka“',
          '„Jen rychle…“',
          '„Dáme to do akčních bodů“',
          '„Kdo dělá zápis?“',
          'Někdo přijde pozdě',
          'Low-hanging fruit',
          'Porada se protáhne',
          '„Můžeš to zopakovat?“',
          'Dítě vběhne do záběru',
          '„Na konci týdne“',
          'Někdo mluví, ale je ztlumený',
          '„Ještě jednu věc…“',
        ],
      },
    },
    {
      blurb: 'Na nedělní oběd u babičky nebo Vánoce s celou rodinou.',
      sample: [2, 5, 10, 13],
      board: {
        title: 'Rodinná oslava',
        size: 4,
        free: false,
        shuffle: true,
        cells: [
          '„Kdy budeš mít děti?“',
          '„Ty jsi ale vyrostl!“',
          'Strejda vypráví stejný vtip',
          'Někdo usne na gauči',
          'Debata o politice',
          '„Ještě si přidej!“',
          '„Za nás bylo líp“',
          'Pes dostane něco ze stolu',
          '„A kolik bereš?“',
          'Fotka na Facebook',
          'Rozbitá sklenička',
          'Hádka o parkování',
          'Zbytky s sebou v krabičce',
          '„Kdy bude svatba?“',
          'Debata o počasí',
          'Někdo přinese stejný dort',
        ],
      },
    },
  ],
  en: [
    {
      blurb: 'For everyone staffing a polling station. Mark off what actually happens in the room.',
      sample: [1, 6, 9, 15],
      board: {
        title: 'Polling station bingo',
        size: 4,
        free: false,
        shuffle: false,
        cells: [
          'The ID app doesn’t work',
          'Voter gives their address instead of their name',
          'Voter comments on the political situation',
          'Voter tries to cast a ballot without the envelope',
          'Selfie at the ballot box',
          'A couple tries to go behind the screen together',
          'Voter takes the ballots outside to study them',
          '“How many people have come so far?”',
          'Election officials can’t do the alphabet',
          '“Who should I vote for?”',
          'A kid drops the envelope into the box',
          'Voter tries to use a driving licence as ID',
          'Voter goes behind the screen with a child',
          '“Should I seal the envelope?”',
          'Expired ID card / passport',
          'Someone spills something',
        ],
      },
    },
    {
      blurb: 'For the video call that could have been an email. Everyone gets a differently shuffled card.',
      sample: [0, 7, 12, 18],
      board: {
        title: 'Meeting bingo',
        size: 5,
        free: true,
        shuffle: true,
        cells: [
          '“Can you hear me?”',
          '“You’re on mute”',
          '“I’ll send it by email”',
          '“Let’s circle back”',
          '“Can you see my screen?”',
          '“Who owns this?”',
          '“Sorry, I have a hard stop”',
          'Synergy',
          'Someone eats on camera',
          'Dog barking in the background',
          'Frozen video',
          '“Let’s take this offline”',
          'Echo',
          '“That’s a great question”',
          '“Just quickly…”',
          '“Let’s add it to the action items”',
          '“Who’s taking notes?”',
          'Someone joins late',
          'Low-hanging fruit',
          'The meeting runs over',
          '“Could you repeat that?”',
          'A kid walks into the frame',
          '“By end of week”',
          'Someone talks while muted',
          '“One more thing…”',
        ],
      },
    },
    {
      blurb: 'For Sunday lunch at grandma’s or the holidays with the whole family.',
      sample: [2, 5, 10, 13],
      board: {
        title: 'Family dinner bingo',
        size: 4,
        free: false,
        shuffle: true,
        cells: [
          '“So when are you having kids?”',
          '“Look how much you’ve grown!”',
          'Uncle tells the same joke again',
          'Someone falls asleep on the sofa',
          'Political argument',
          '“Have some more!”',
          '“Things were better in our day”',
          'The dog gets food from the table',
          '“So how much do you earn?”',
          'Photo for Facebook',
          'A broken glass',
          'Argument about parking',
          'Leftovers to take home',
          '“So when’s the wedding?”',
          'Small talk about the weather',
          'Two people bring the same cake',
        ],
      },
    },
  ],
};
